import React from 'react';

export default function DashboardCard({ children, className = '', onClick, noPadding = false, animDelay = 0 }) {
  return (
    <>
      <style>{`
        @keyframes card-enter {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        .card-enter {
          animation: card-enter 0.3s cubic-bezier(0.4,0,0.2,1) both;
        }
        @keyframes pill-pop {
          0%   { transform: scale(0.85); opacity: 0; }
          60%  { transform: scale(1.05); }
          100% { transform: scale(1);    opacity: 1; }
        }
        .pill-pop {
          animation: pill-pop 0.25s cubic-bezier(0.34,1.56,0.64,1) both;
        }
      `}</style>
      <div
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
        onClick={onClick}
        style={{ animationDelay: `${animDelay}ms` }}
        className={[
          'card-enter',
          'bg-white rounded-[12px] border border-[#E2E8ED]',
          'shadow-[0_1px_3px_rgba(2,45,65,0.06),0_4px_12px_rgba(2,45,65,0.06)]',
          'flex flex-col overflow-hidden',
          onClick ? 'cursor-pointer hover:shadow-[0_4px_20px_rgba(2,45,65,0.13)] hover:-translate-y-[2px] transition-all duration-200' : '',
          className,
        ].filter(Boolean).join(' ')}
      >
        {children}
      </div>
    </>
  );
}

export function CardHeader({ icon, title, action }) {
  return (
    <div className="flex items-center justify-between px-5 pt-5 pb-3.5">
      <div className="flex items-center gap-2">
        {icon && (
          <span className="flex items-center text-[#0094B3]">
            {React.cloneElement(icon, { sx: { fontSize: 18 } })}
          </span>
        )}
        <h2 className="text-[16px] font-semibold text-[#022D41] m-0 leading-snug">{title}</h2>
      </div>
      {action && (
        <span className="text-[14px] text-[#0094B3] font-medium cursor-pointer hover:text-[#0081A0] transition-colors shrink-0">
          {action}
        </span>
      )}
    </div>
  );
}

export function StatusPill({ status }) {
  const statusStr = typeof status === 'object' && status !== null
    ? (status.Status ?? status.ApproverUIStatus ?? '')
    : (status ?? '');

  const styles = {
    Pending:   { bg: 'bg-[#FEF3C7]', text: 'text-[#D97706]', dot: 'bg-[#F59E0B]' },
    Approved:  { bg: 'bg-[#D1FAE5]', text: 'text-[#059669]', dot: 'bg-[#10B981]' },
    Rejected:  { bg: 'bg-[#FEE2E2]', text: 'text-[#DC2626]', dot: 'bg-[#EF4444]' },
    Draft:     { bg: 'bg-[#F1F5F9]', text: 'text-[#64748B]', dot: 'bg-[#94A3B8]' },
    Review:    { bg: 'bg-[#E6F4F8]', text: 'text-[#0094B3]', dot: 'bg-[#0094B3]' },
    Submitted: { bg: 'bg-[#E6F4F8]', text: 'text-[#0094B3]', dot: 'bg-[#0094B3]' },
    Shortlisted: { bg: 'bg-purple-50', text: 'text-purple-600', dot: 'bg-purple-500' },
  };
  const s = styles[statusStr] ?? styles.Draft;

  if (!statusStr) return null;

  return (
    <span className={`pill-pop inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[12px] font-semibold ${s.bg} ${s.text}`}>
      <span className={`w-[5px] h-[5px] rounded-full shrink-0 ${s.dot}`} />
      {statusStr}
    </span>
  );
}

export function OpenLink({ onClick }) {
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      className="flex items-center gap-1 text-[14px] text-[#0094B3] font-medium bg-transparent border-none cursor-pointer p-0 hover:text-[#0081A0] transition-colors shrink-0"
    >
      <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor">
        <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
        <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
      </svg>
      Open
    </button>
  );
}

export function CardDivider() {
  return <div className="h-px bg-[#E2E8ED] mx-5" />;
}

export function ColHeader({ cols }) {
  return (
    <div className="flex items-center px-5 py-2 bg-[#F8FAFB] border-y border-[#E2E8ED]">
      {cols.map(({ label, flex = 1 }) => (
        <span
          key={label}
          className="text-[12px] font-semibold text-[#6B7A85] uppercase tracking-wider"
          style={{ flex }}
        >
          {label}
        </span>
      ))}
    </div>
  );
}

export function PrimaryButton({ children, onClick, icon, variant = 'dark' }) {
  const base = 'flex items-center justify-center gap-2 w-full py-3.5 text-[15px] font-semibold border-none transition-all duration-150 active:scale-[0.98]';
  const variants = {
    dark:     `${base} bg-[#022D41] text-white cursor-pointer hover:bg-[#034060]`,
    teal:     `${base} bg-[#0094B3] text-white cursor-pointer hover:bg-[#0081A0]`,
    disabled: `${base} bg-[#E2E8ED] text-[#6B7A85] cursor-not-allowed`,
  };
  return (
    <button onClick={onClick} className={variants[variant]}>
      {icon && <span className="text-base leading-none">{icon}</span>}
      {children}
    </button>
  );
}

export function EmptyState({ message }) {
  return (
    <div className="flex flex-col items-center justify-center py-10 gap-2 text-[#6B7A85]">
      <svg width="32" height="32" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5" className="opacity-30">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
      </svg>
      <span className="text-[14px]">{message}</span>
    </div>
  );
}